/**
 * Entidad StoreCustomization
 * Configuración de la tienda
 */

import { Media } from "../common";

/**
 * Tipos para la navegación personalizable del menú
 */
export enum NavigationItemType {
  HOME = 'HOME',
  CATALOG = 'CATALOG',
  CATEGORY = 'CATEGORY',
  COLLECTION = 'COLLECTION',
  PAGE = 'PAGE',
  CUSTOM = 'CUSTOM',
}

export interface NavigationMenuItem {
  id: string;
  label: string;
  itemType: NavigationItemType;
  referenceId?: string;
  customUrl?: string;
  order: number;
  includeChildren?: boolean;
  highlight?: boolean;
  highlightColor?: string;
  openInNewTab?: boolean;
}

export interface NavigationMenuConfig {
  items: NavigationMenuItem[];
  autoFallback?: boolean;
}

/**
 * Zona del Header: componentes antes y después del header principal
 */
export interface StoreCustomizationHeaderZone {
  header: StoreCustomizationLayoutComponent;
  preComponents: StoreCustomizationLayoutComponent[];
  postComponents: StoreCustomizationLayoutComponent[];
}

/**
 * Zona del Footer: componentes antes y después del footer principal
 */
export interface StoreCustomizationFooterZone {
  footer: StoreCustomizationLayoutComponent;
  preComponents: StoreCustomizationLayoutComponent[];
  postComponents: StoreCustomizationLayoutComponent[];
}

/**
 * Nueva estructura de layout con zonas
 */
export interface StoreCustomizationLayout {
  headerZone: StoreCustomizationHeaderZone;
  footerZone: StoreCustomizationFooterZone;
}

export function isZonedLayout(layout: unknown): layout is StoreCustomizationLayout {
  return !!(
    layout &&
    typeof layout === 'object' &&
    'headerZone' in layout &&
    'footerZone' in layout
  );
}

export interface StoreCustomization {
  id: string;
  accountId: string;
  layout: StoreCustomizationLayout;
  pages: StoreCustomizationPage[];
  theme: {
    colors: StoreCustomizationThemeColors;
    typography: StoreCustomizationThemeTypography;
    buttons: StoreCustomizationThemeButtons;
    designTokens?: DesignTokens;
  };
  seo: StoreCustomizationSeo;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

export interface StoreCustomizationLayoutComponent {
  id: string;
  type: string;
  name: string;
  version: string;
  settings: StoreCustomizationComponentSettings;
}

export interface StoreCustomizationPageComponent {
  id: string;
  type: string;
  name: string;
  version: string;
  settings: StoreCustomizationComponentSettings;
}

/** Builder page entry. For CMS pages (store_page type OTHER), id matches store_page.id. */
export interface StoreCustomizationPage {
  id: string;
  name: string;
  path: string;
  components: StoreCustomizationPageComponent[];
}

export interface StoreCustomizationComponentSettings {
  [key: string]: any;
}

export interface StoreCustomizationThemeColors {
  background: {
      backgroundColor: string;
  };
  text: {
      titleTextColor: string;
      bodyTextColor: string;
  };
  theme: {
      primaryColor: string;
      secondaryColor: string;
  };
}

export interface StoreCustomizationThemeTypography {
  headings: {
      fontFamily: string;
      fontSizeBase: number;
      fontUppercase: boolean;
  };
  body: {
      fontFamily: string;
      fontSizeBase: number;
      fontUppercase: boolean;
  };

}

export interface ButtonStyle {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  /** CSS string e.g. "6px 6px 6px 6px" */
  borderRadius: string;
  shadow: string;
  hoverBackgroundColor: string;
  hoverTextColor: string;
  hoverBorderColor: string;
  hoverEffect: string;
}

export interface LinkButtonStyle extends ButtonStyle {
  textDecoration?: string;
  hoverTextDecoration?: string;
}

export interface StoreCustomizationThemeButtons {
  primary: ButtonStyle;
  secondary: ButtonStyle;
  outline: ButtonStyle;
  link: LinkButtonStyle;
  success: ButtonStyle;
  warning: ButtonStyle;
  danger: ButtonStyle;
}

// ============================================
// DESIGN TOKENS - Sistema de Theme expandido
// ============================================

/**
 * Variante de color con sus estados
 */
export interface ColorVariant {
  default: string;
  light: string;
  dark: string;
  contrast: string;
}

/**
 * Escala de colores neutrales (50-900)
 */
export interface NeutralColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

/**
 * Colores de superficie (fondos)
 */
export interface SurfaceColors {
  background: string;
  card: string;
  overlay: string;
}

/**
 * Paleta de colores completa del theme
 */
export interface ThemeColorPalette {
  primary: ColorVariant;
  secondary: ColorVariant;
  accent: ColorVariant;
  neutral: NeutralColorScale;
  success: ColorVariant;
  warning: ColorVariant;
  error: ColorVariant;
  info: ColorVariant;
  surface: SurfaceColors;
}

/**
 * Estilo de tipografía predefinido
 */
export interface TypographyStyle {
  size: string;
  weight: string;
  lineHeight: number;
  letterSpacing?: string;
}

/**
 * Estilos de tipografía disponibles
 */
export interface ThemeTypographyStyles {
  display: TypographyStyle;
  'heading-1': TypographyStyle;
  'heading-2': TypographyStyle;
  'heading-3': TypographyStyle;
  'heading-4': TypographyStyle;
  'body-large': TypographyStyle;
  body: TypographyStyle;
  'body-small': TypographyStyle;
  caption: TypographyStyle;
  label: TypographyStyle;
}

/**
 * Escala de tamaños de tipografía
 */
export interface ThemeTypographySizes {
  xs: string;
  sm: string;
  base: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
  '5xl': string;
}

/**
 * Fuentes del theme
 */
export interface ThemeFonts {
  heading: string;
  body: string;
}

/**
 * Configuración completa de tipografía
 */
export interface ThemeTypography {
  styles: ThemeTypographyStyles;
  sizes: ThemeTypographySizes;
  fonts: ThemeFonts;
}

/**
 * Estilo de variante de botón
 */
export interface ThemeButtonVariant {
  background: string;
  text: string;
  border: string;
  hoverBackground: string;
  hoverText?: string;
  hoverBorder?: string;
}

/**
 * Tamaño de botón
 */
export interface ThemeButtonSize {
  padding: string;
  fontSize: string;
  borderRadius: string;
}

/**
 * Variantes de botones disponibles
 */
export interface ThemeButtonVariants {
  primary: ThemeButtonVariant;
  secondary: ThemeButtonVariant;
  outline: ThemeButtonVariant;
  ghost: ThemeButtonVariant;
  danger: ThemeButtonVariant;
  success: ThemeButtonVariant;
}

/**
 * Tamaños de botones disponibles
 */
export interface ThemeButtonSizes {
  sm: ThemeButtonSize;
  md: ThemeButtonSize;
  lg: ThemeButtonSize;
}

/**
 * Configuración completa de botones
 */
export interface ThemeButtons {
  variants: ThemeButtonVariants;
  sizes: ThemeButtonSizes;
}

/**
 * Escala de espaciado
 */
export interface ThemeSpacing {
  none: string;
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
}

/**
 * Escala de border radius
 */
export interface ThemeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  full: string;
}

/**
 * Escala de border width
 */
export interface ThemeBorderWidth {
  none: string;
  thin: string;
  medium: string;
  thick: string;
}

/**
 * Configuración completa de bordes
 */
export interface ThemeBorders {
  radius: ThemeBorderRadius;
  width: ThemeBorderWidth;
}

/**
 * Escala de sombras
 */
export interface ThemeShadows {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

/**
 * Configuración de Product Cards
 */
export interface ThemeProductCard {
  borderRadius: string;
  shadow: string;
  padding: string;
  imageAspectRatio: string;
  showBorder: boolean;
  borderColor: string;
  hoverEffect: string;
  titleSize: string;
  titleWeight: string;
  priceSize: string;
  priceWeight: string;
}

/**
 * Design Tokens completo
 */
export interface DesignTokens {
  colors: ThemeColorPalette;
  typography: ThemeTypography;
  buttons: ThemeButtons;
  spacing: ThemeSpacing;
  borders: ThemeBorders;
  shadows: ThemeShadows;
  productCard: ThemeProductCard;
}


export interface StoreCustomizationSeo {
  pageTitle: string;
  pageSlogan: string;
  pageDescription: string;
  pageKeywords: string;
  
  pageImageId?: string | null;
  pageImage?: Media | null;
  faviconId?: string | null;
  favicon?: Media | null;
}

 